import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";

import { toast } from "react-toastify";
import logo6 from "./logo-6.png";

// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
  


  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: end;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;






const DateInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 10px;
    color: ${palette.headText};
    opacity: 0.85;
    text-align: left;
  }

  input {
    padding: 4px 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background-color: rgba(255, 255, 255, 0.1);
    color: ${palette.headText};
    font-size: 12px;
    width: 120px;

    &:focus {
      outline: none;
      border-color: ${palette.accent};
      box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.3);
    }

    @media (max-width: 780px) {
      width: 100px;
      font-size: 11px;
    }
  }
`;

// const fmt = (value, min = 0, max = 1) => {
//   // Se for zero/NaN/undefined, retorna "0,00" ou "0" dependendo dos dígitos
//   const num = Number(value) || 0;
//   return new Intl.NumberFormat("pt-BR", {
//     minimumFractionDigits: min,
//     maximumFractionDigits: max,
//   }).format(num);
// };


const fmt = (value, min = 0, max = 1) => {
  // se value for '-' ou string vazia ou null/undefined => retorna vazio (linha separadora)
  if (value === "-" || value === null || value === undefined || value === "") return "";

  // tenta converter para número (aceita strings numéricas)
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(n);
};




const Grid = ({ zoomIn, zoomOut, zoom, urlApi }) => {
  const [dataIni, setDataIni] = useState(() => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = "01";
    return `${yyyy}-${mm}-${dd}`;
  });


  const [dataFim, setDataFim] = useState(() => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [horaRodou, setHoraRodou] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Separate state for each metric
  const [moagem, setMoagem] = useState([]);
  const [bioTempoDisponivel, setBioTempoDisponivel] = useState([]);
  const [bioTempoPerdidoMoenda, setBioTempoPerdidoMoenda] = useState([]);
  const [bioHorasEfetivasTotais, setBioHorasEfetivasTotais] = useState([]);
  const [bioTempoPerdidoCaldeiraUm, setBioTempoPerdidoCaldeiraUm] = useState([]);
  const [bioTempoPerdidoCaldeiraDois, setBioTempoPerdidoCaldeiraDois] = useState([]);
  const [bioTotalVaporGeradoUm, setBioTotalVaporGeradoUm] = useState([]);
  const [bioTotalVaporGeradoDois, setBioTotalVaporGeradoDois] = useState([]);
  const [bioFibra, setBioFibra] = useState([]);
  const [bioImpurezaMineral, setBioImpurezaMineral] = useState([]);
  const [bioUmidadeBagaco, setBioUmidadeBagaco] = useState([]);
  const [bioMassaComprada, setBioMassaComprada] = useState([]);

  const fetchDados = async (tipo, setState) => {
    if (dataIni && dataFim) {
      const startDate = new Date(dataIni);
      const endDate = new Date(dataFim);
      if (endDate < startDate) {
        toast.error(`⚠️ Data final (${dataFim}) é anterior à data inicial (${dataIni}).`);
        setState([]);
        return;
      }
    }

    try {
      const res = await axios.get(`${urlApi}boletins-externo/atualiza-biomassa/${tipo}`, {
        params: { data_ini: dataIni, data_fim: dataFim },
      });

      const ordem = ["mes", "safra"];
      if (Array.isArray(res.data)) {
        const dadosOrdenados = res.data.sort((a, b) =>
          ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO)
        );
        setState(dadosOrdenados);
      } else {
        console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
        setState([]);
      }
    } catch (error) {
      console.error(`Erro ao buscar ${tipo}:`, error);
      setState([]);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchDados("bio-moagem", setMoagem),
      fetchDados("bio-tempo-disponivel", setBioTempoDisponivel),
      fetchDados("bio-tempo-perdido-moenda", setBioTempoPerdidoMoenda),
      fetchDados("bio-horas-efetivas-totais", setBioHorasEfetivasTotais),
      fetchDados("bio-tempo-perdido-caldeira-um", setBioTempoPerdidoCaldeiraUm),
      fetchDados("bio-tempo-perdido-caldeira-dois", setBioTempoPerdidoCaldeiraDois),
      fetchDados("bio-total-vapor-gerado-caldeira-um", setBioTotalVaporGeradoUm),
      fetchDados("bio-total-vapor-gerado-caldeira-dois", setBioTotalVaporGeradoDois),
      fetchDados("bio-fibra", setBioFibra),
      fetchDados("bio-impureza-mineral", setBioImpurezaMineral),
      fetchDados("bio-umidade-bagaco", setBioUmidadeBagaco),
      fetchDados("bio-massa-comprada", setBioMassaComprada),
    ]);
    setLoading(false);
  };

  // Utility to extract month number from dataFim
  const getMonthFromDataFim = () => {
    if (!dataFim) return "-";
    const [, month] = dataFim.split("-");
    return parseInt(month, 10);
  };

  useEffect(() => {
    const allDataLoaded =
      moagem.length > 0 &&
      bioTempoDisponivel.length > 0 &&
      bioTempoPerdidoMoenda.length > 0 &&
      bioHorasEfetivasTotais.length > 0 &&
      bioTempoPerdidoCaldeiraUm.length > 0 &&
      bioTempoPerdidoCaldeiraDois.length > 0 &&
      bioTotalVaporGeradoUm.length > 0 &&
      bioTotalVaporGeradoDois.length > 0 &&
      bioFibra.length > 0 &&
      bioImpurezaMineral.length > 0 &&
      bioUmidadeBagaco.length > 0 &&
      bioMassaComprada.length > 0;

    if (allDataLoaded) {
      setHoraRodou(
        new Date().toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );

      const fetchedData = {
        moagem: Number(moagem.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        moagemMes: Number(moagem.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        tempoDisponivel: Number(bioTempoDisponivel.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        tempoDisponivelMes: Number(bioTempoDisponivel.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        tempoPerdidoMoenda: Number(bioTempoPerdidoMoenda.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        tempoPerdidoMoendaMes: Number(bioTempoPerdidoMoenda.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        horasEfetivasTotais: Number(bioHorasEfetivasTotais.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        horasEfetivasTotaisMes: Number(bioHorasEfetivasTotais.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        tempoPerdidoCal1: Number(bioTempoPerdidoCaldeiraUm.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        tempoPerdidoCal1Mes: Number(bioTempoPerdidoCaldeiraUm.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        tempoPerdidoCal2: Number(bioTempoPerdidoCaldeiraDois.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        tempoPerdidoCal2Mes: Number(bioTempoPerdidoCaldeiraDois.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        vaporCal1: Number(bioTotalVaporGeradoUm.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        vaporCal1Mes: Number(bioTotalVaporGeradoUm.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        vaporCal2: Number(bioTotalVaporGeradoDois.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        vaporCal2Mes: Number(bioTotalVaporGeradoDois.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        fibra: Number(bioFibra.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        fibraMes: Number(bioFibra.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        impurezaMineral: Number(bioImpurezaMineral.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        impurezaMineralMes: Number(bioImpurezaMineral.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        umidadeBagaco: Number(bioUmidadeBagaco.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        umidadeBagacoMes: Number(bioUmidadeBagaco.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
        biomassaComprada: Number(bioMassaComprada.find((item) => item.PERIODO === "safra")?.VALOR) || 0,
        biomassaCompradaMes: Number(bioMassaComprada.find((item) => item.PERIODO === "mes")?.VALOR) || 0,
      };

      // Calculations (same as your original code)
      const dispGeralMoenda = (((fetchedData.tempoDisponivel - fetchedData.tempoPerdidoMoenda) / fetchedData.tempoDisponivel) * 100).toFixed(1);
      const dispGeralCal1 = ((fetchedData.tempoDisponivel - fetchedData.tempoPerdidoCal1) / fetchedData.tempoDisponivel * 100).toFixed(1);
      const dispGeralCal2 = ((fetchedData.tempoDisponivel - fetchedData.tempoPerdidoCal2) / fetchedData.tempoDisponivel * 100).toFixed(1);
      const tempoPerdidoCaldeiras = fetchedData.tempoPerdidoCal1 + fetchedData.tempoPerdidoCal2;
      const totalVapor = fetchedData.vaporCal1 + fetchedData.vaporCal2;
      const moagemHora = fetchedData.horasEfetivasTotais === 0 ? 0 : (fetchedData.moagem / fetchedData.horasEfetivasTotais).toFixed(1);
      const fatorConsumoVapor = moagemHora === 0 ? 0 : (totalVapor / (fetchedData.tempoDisponivel - tempoPerdidoCaldeiras) / moagemHora).toFixed(1);
      const consumoVaporCana = (moagemHora * 0.45).toFixed(1);
      const bagacoConsumidoHora = (consumoVaporCana / 2.1).toFixed(0);
      const fatorDescontoFibra = fetchedData.fibra > 16 ? 15 : fetchedData.fibra > 15 ? 10 : fetchedData.fibra > 14 ? 5 : 0;
      const fibraLiquida = (fetchedData.fibra * (1 - fatorDescontoFibra / 100) - fetchedData.impurezaMineral).toFixed(1);
      const bagacoProduzido = fetchedData.umidadeBagaco !== 0 ? (moagemHora * fibraLiquida / 100) / ((100 - fetchedData.umidadeBagaco) / 100) : 0;
      const bagacoExcedente = (bagacoProduzido - bagacoConsumidoHora).toFixed(1);
      const bagacoProduzidoTotal = (fetchedData.horasEfetivasTotais * bagacoProduzido).toFixed(0);
      const bagacoConsumidoTotal = (fetchedData.horasEfetivasTotais * bagacoConsumidoHora).toFixed(0);
      const bagacoExcedenteTotal = (fetchedData.horasEfetivasTotais * bagacoExcedente).toFixed(0);
      const custoCctMp = fetchedData.moagem * 160;
      const custoCaldo = ((100 - fibraLiquida) * custoCctMp / 100).toFixed(0);
      const custoBagaco = (fibraLiquida * custoCctMp / 100).toFixed(0);
      const custoBagacoProduzido = bagacoProduzidoTotal === 0 ? 0 : (custoBagaco / bagacoProduzidoTotal).toFixed(1);
      const custoBagacoConsumido = (custoBagacoProduzido * bagacoConsumidoTotal).toFixed(0);
      const bagacoConsiderandoPerdas = (bagacoExcedenteTotal * 0.85).toFixed(1);
      const custoBagacoExcedente = (custoBagacoProduzido * bagacoExcedenteTotal).toFixed(0);
      const biomassaTotal = (Number(bagacoConsiderandoPerdas) + fetchedData.biomassaComprada).toFixed(1);


      const dispGeralMoendaMes = fetchedData.tempoDisponivelMes === 0 ? 0 : (((fetchedData.tempoDisponivelMes - fetchedData.tempoPerdidoMoendaMes) / fetchedData.tempoDisponivelMes) * 100).toFixed(1);
      const dispGeralCal1Mes = fetchedData.tempoDisponivelMes === 0 ? 0 : ((fetchedData.tempoDisponivelMes - fetchedData.tempoPerdidoCal1Mes) / fetchedData.tempoDisponivelMes * 100).toFixed(1);
      const dispGeralCal2Mes = fetchedData.tempoDisponivelMes === 0 ? 0 : ((fetchedData.tempoDisponivelMes - fetchedData.tempoPerdidoCal2Mes) / fetchedData.tempoDisponivelMes * 100).toFixed(1);
      const tempoPerdidoCaldeirasMes = fetchedData.tempoPerdidoCal1Mes + fetchedData.tempoPerdidoCal2Mes;
      const totalVaporMes = fetchedData.vaporCal1Mes + fetchedData.vaporCal2Mes;
      const moagemHoraMes = fetchedData.horasEfetivasTotaisMes === 0 ? 0 : (fetchedData.moagemMes / fetchedData.horasEfetivasTotaisMes).toFixed(1);
      const fatorConsumoVaporMes = moagemHoraMes === 0 ? 0 : (totalVaporMes / (fetchedData.tempoDisponivelMes - tempoPerdidoCaldeirasMes) / moagemHoraMes).toFixed(1);
      const consumoVaporCanaMes = (moagemHoraMes * 0.45).toFixed(1);
      const bagacoConsumidoHoraMes = (consumoVaporCanaMes / 2.1).toFixed(0);
      const fatorDescontoFibraMes = fetchedData.fibraMes > 16 ? 15 : fetchedData.fibraMes > 15 ? 10 : fetchedData.fibraMes > 14 ? 5 : 0;
      const fibraLiquidaMes = (fetchedData.fibraMes * (1 - fatorDescontoFibraMes / 100) - fetchedData.impurezaMineralMes).toFixed(1);
      const bagacoProduzidoMes = fetchedData.umidadeBagacoMes !== 0 ? (moagemHoraMes * fibraLiquidaMes / 100) / ((100 - fetchedData.umidadeBagacoMes) / 100) : 0;
      const bagacoExcedenteMes = (bagacoProduzidoMes - bagacoConsumidoHoraMes).toFixed(1);
      const bagacoProduzidoTotalMes = (fetchedData.horasEfetivasTotaisMes * bagacoProduzidoMes).toFixed(0);
      const bagacoConsumidoTotalMes = (fetchedData.horasEfetivasTotaisMes * bagacoConsumidoHoraMes).toFixed(0);
      const bagacoExcedenteTotalMes = (fetchedData.horasEfetivasTotaisMes * bagacoExcedenteMes).toFixed(0);
      const custoCctMpMes = fetchedData.moagemMes * 160;
      const custoCaldoMes = ((100 - fibraLiquidaMes) * custoCctMpMes / 100).toFixed(0);
      const custoBagacoMes = (fibraLiquidaMes * custoCctMpMes / 100).toFixed(0);
      const custoBagacoProduzidoMes = bagacoProduzidoTotalMes === 0 ? 0 : (custoBagacoMes / bagacoProduzidoTotalMes).toFixed(1);
      const custoBagacoConsumidoMes = (custoBagacoProduzidoMes * bagacoConsumidoTotalMes).toFixed(0);
      const bagacoConsiderandoPerdasMes = (bagacoExcedenteTotalMes * 0.85).toFixed(1);
      const custoBagacoExcedenteMes = (custoBagacoProduzidoMes * bagacoExcedenteTotalMes).toFixed(0);
      const biomassaTotalMes = (Number(bagacoConsiderandoPerdasMes) + fetchedData.biomassaCompradaMes).toFixed(1);


      const tableData = [
        { desc: "Moagem", unit: "Ton", safra: fetchedData.moagem, mes: fetchedData.moagemMes },
        { desc: "Tempo Disponivel", unit: "H", safra: fetchedData.tempoDisponivel, mes: fetchedData.tempoDisponivelMes },
        { desc: "Tempo Perdido na Moenda", unit: "H", safra: fetchedData.tempoPerdidoMoenda, mes: fetchedData.tempoPerdidoMoendaMes },
        { desc: "Horas Efetivas Totais", unit: "H", safra: fetchedData.horasEfetivasTotais, mes: fetchedData.horasEfetivasTotaisMes },
        { desc: "Disponibilidade Geral - Moenda", unit: "%", safra: dispGeralMoenda, mes: dispGeralMoendaMes },
        { desc: "Tempo Perdido na Caldeira 1", unit: "H", safra: fetchedData.tempoPerdidoCal1, mes: fetchedData.tempoPerdidoCal1Mes },
        { desc: "Disponibilidade Geral - Caldeira 1", unit: "%", safra: dispGeralCal1, mes: dispGeralCal1Mes },
        { desc: "Tempo Perdido na Caldeira 2", unit: "H", safra: fetchedData.tempoPerdidoCal2, mes: fetchedData.tempoPerdidoCal2Mes },
        { desc: "Disponibilidade Geral - Caldeira 2", unit: "%", safra: dispGeralCal2, mes: dispGeralCal2Mes },
        { desc: "Tempo Perdido Total nas Caldeiras", unit: "H", safra: tempoPerdidoCaldeiras, mes: tempoPerdidoCaldeirasMes },
        { desc: "Total de Vapor Gerado na Caldeira 1", unit: "Ton", safra: fetchedData.vaporCal1, mes: fetchedData.vaporCal1Mes },
        { desc: "Total de Vapor Gerado na Caldeira 2", unit: "Ton", safra: fetchedData.vaporCal2, mes: fetchedData.vaporCal2Mes },
        { desc: "Totalizador de Vapor Gerado", unit: "Ton", safra: totalVapor, mes: totalVaporMes },
        { desc: "Moagem / Hora", unit: "Ton/H", safra: moagemHora, mes: moagemHoraMes },
        { desc: "Fator de Consumo de Vapor", unit: "Ton/H", safra: fatorConsumoVapor, mes: fatorConsumoVaporMes },
        { desc: "Consumo de Vapor para Cana (Processo Cana)", unit: "Ton/H", safra: consumoVaporCana, mes: consumoVaporCanaMes },
        { desc: "Bagaço Consumido por Hora (Processo Cana)", unit: "Ton/H", safra: bagacoConsumidoHora, mes: bagacoConsumidoHoraMes },
        { separator: true },
        { desc: "Fibra", unit: "%", safra: fetchedData.fibra, mes: fetchedData.fibraMes },
        { desc: "Fator de Desconto - Fibra", unit: "%", safra: fatorDescontoFibra, mes: fatorDescontoFibraMes },
        { desc: "Impureza Mineral", unit: "%", safra: fetchedData.impurezaMineral, mes: fetchedData.impurezaMineralMes },
        { desc: "Fibra Líquida", unit: "%", safra: fibraLiquida, mes: fibraLiquidaMes },
        { desc: "Umidade Bagaço", unit: "%", safra: fetchedData.umidadeBagaco, mes: fetchedData.umidadeBagacoMes },
        { desc: "Bagaço Produzido", unit: "Ton/H", safra: bagacoProduzido, mes: bagacoProduzidoMes },
        { separator: true },
        { desc: "Bagaço Excedente", unit: "Ton/H", safra: bagacoExcedente, mes: bagacoExcedenteMes },
        { separator: true },
        { desc: "Bagaço Produzido Total", unit: "Ton", safra: bagacoProduzidoTotal, mes: bagacoProduzidoTotalMes },
        { desc: "Bagaço Consumido Total (Processo Cana)", unit: "Ton", safra: bagacoConsumidoTotal, mes: bagacoConsumidoTotalMes },
        { desc: "Bagaço Excedente Total", unit: "Ton", safra: bagacoExcedenteTotal, mes: bagacoExcedenteTotalMes },
        { separator: true },
        { desc: "Custo CCT + MP", unit: "R$", safra: custoCctMp, mes: custoCctMpMes },
        { desc: "Custo Caldo", unit: "R$", safra: custoCaldo, mes: custoCaldoMes },
        { desc: "Custo Bagaço", unit: "R$", safra: custoBagaco, mes: custoBagacoMes },
        { separator: true },
        { desc: "Custo Bagaço Produzido", unit: "R$/Ton", safra: custoBagacoProduzido, mes: custoBagacoProduzidoMes },
        { separator: true },
        { desc: "Custo Bagaço Consumido (Processo Cana)", unit: "R$", safra: custoBagacoConsumido, mes: custoBagacoConsumidoMes },
        { desc: "Custo Bagaço Excedente", unit: "R$", safra: custoBagacoExcedente, mes: custoBagacoExcedenteMes },
        { separator: true },
        { desc: "Bagaço Considerando Perdas", unit: "Ton", safra: bagacoConsiderandoPerdas, mes: bagacoConsiderandoPerdasMes },
        { desc: "Biomassa Comprada", unit: "Ton", safra: fetchedData.biomassaComprada, mes: fetchedData.biomassaCompradaMes },
        { desc: "Biomassa Total Disponível", unit: "Ton", safra: biomassaTotal, mes: biomassaTotalMes },
      ];

      setData(tableData);
    }
  }, [
    moagem,
    bioTempoDisponivel,
    bioTempoPerdidoMoenda,
    bioHorasEfetivasTotais,
    bioTempoPerdidoCaldeiraUm,
    bioTempoPerdidoCaldeiraDois,
    bioTotalVaporGeradoUm,
    bioTotalVaporGeradoDois,
    bioFibra,
    bioImpurezaMineral,
    bioUmidadeBagaco,
    bioMassaComprada,
  ]);

  const gerarPdfRelatorio = () => {
    const doc = new jsPDF();
    doc.addImage(logo6, "PNG", 140, 10, 50, 20);
    doc.setFontSize(14);
    doc.text("Boletim Biomassa", 105, 15, null, null, "center");
    doc.setFontSize(10);
    doc.text("Atualizado: " + horaRodou, 105, 22, null, null, "center");

    const startY = 30;
    autoTable(doc, {
      startY,
      head: [["Descrição", "Unid", "Safra", `${getMonthFromDataFim()}º Mês`]],
      body: data.map((item) => [
        item.desc,
        item.unit,
        typeof item.safra === "number" ? new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.safra) : String(item.safra),
        typeof item.mes === "number" ? new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.mes) : String(item.mes),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [10, 122, 63] },
      theme: "striped",
      margin: { left: 8, right: 8 },
    });

    const safeTime = (horaRodou || "").replace(/[:\/\s]/g, "_").replace(/_+/g, "_");
    doc.save(`biomassa_${safeTime || "relatorio"}.pdf`);
  };

  return (
    <Geral>
      <div>
        <Table>
          <Thead>
            <Tr noHover>
              <CabecalhoTabela colSpan={4}>
                <HeaderWrapper>
                  <h1>Boletim da Biomassa</h1>
                </HeaderWrapper>
                <HeaderWrapper style={{ paddingTop: "20px" }}>
                  <HeaderRight>
                    <IconGroup>
                      <DateInputWrapper>
                        <label>Data Início</label>
                        <input
                          type="date"
                          value={dataIni}
                          onChange={(e) => setDataIni(e.target.value)}

                        />
                      </DateInputWrapper>
                      <DateInputWrapper>
                        <label>Data Fim</label>
                        <input
                          type="date"
                          value={dataFim}
                          onChange={(e) => setDataFim(e.target.value)}
                        />
                      </DateInputWrapper>

                      <Button onClick={fetchAllData}>Buscar</Button>
                    </IconGroup>
                  </HeaderRight>
                </HeaderWrapper>
                <HeaderWrapper style={{ paddingTop: "20px" }}>
                  <h2></h2>
                  <HeaderRight>
                    <p>Atualizado: {horaRodou}</p>
                    <IconGroup>
                      <Button onClick={gerarPdfRelatorio}>
                        <FaDownload />
                      </Button>
                      <Button onClick={zoomOut} title="Diminuir zoom (Alt+-)" aria-label="Diminuir zoom" type="button">
                        <FiZoomOut />
                      </Button>
                      <Button onClick={zoomIn} title="Aumentar zoom (Alt+=)" aria-label="Aumentar zoom" type="button">
                        <FiZoomIn />
                      </Button>
                    </IconGroup>
                  </HeaderRight>
                </HeaderWrapper>
              </CabecalhoTabela>
            </Tr>
            <Tr noHover>
              <Th>Descrição</Th>
              <Th>Unid</Th>
              <Th>Mês {getMonthFromDataFim()}º</Th>
              <Th>Safra</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={4} alignCenter>
                  Carregando dados...
                </Td>
              </Tr>
            ) : data.length > 0 ? (
              data.map((item, index) => {
                if (item.separator) {
                  // linha separadora — uma linha vazia com espaçamento
                  return (
                    <Tr key={`sep-${index}`}>
                      <Td colSpan={4} style={{ padding: "8px 0", height: 8 }} />
                    </Tr>
                  );
                }

                // cell format logic
                const formatMes = () => {
                  if (item.unit === "R$") return fmt(item.mes, 0, 0);        // sem casas decimais
                  if (item.unit === "%") return fmt(item.mes, 1, 1);
                  if (item.unit === "H") return fmt(item.mes, 1, 1);
                  if (item.unit === "Ton") return fmt(item.mes, 1, 1);
                  if (item.unit === "R$/Ton") return fmt(item.mes, 1, 1);
                  if (item.unit && item.unit.includes("Ton") && item.unit.includes("/H")) return fmt(item.mes, 1, 1);
                  return fmt(item.mes, 2, 2);
                };

                const formatSafra = () => {
                  if (item.unit === "R$") return fmt(item.safra, 0, 0);     // sem casas decimais
                  if (item.unit === "%") return fmt(item.safra, 1, 1);
                  if (item.unit === "H") return fmt(item.safra, 1, 1);
                  if (item.unit === "Ton") return fmt(item.safra, 1, 1);
                  if (item.unit === "R$/Ton") return fmt(item.safra, 1, 1);
                  if (item.unit && item.unit.includes("Ton") && item.unit.includes("/H")) return fmt(item.safra, 1, 1);
                  return fmt(item.safra, 2, 2);
                };

                return (
                  <Tr key={index}>
                    <Td zebra={index % 2 === 0}>
                      <strong>{item.desc}</strong>
                    </Td>
                    <Td alignCenter zebra={index % 2 === 0}>
                      {item.unit}
                    </Td>
                    <Td alignCenter zebra={index % 2 === 0}>
                      {formatMes()}
                    </Td>
                    <Td alignCenter zebra={index % 2 === 0}>
                      {formatSafra()}
                    </Td>
                  </Tr>
                );
              })

            ) : (
              <Tr>
                <Td colSpan={4} alignCenter>
                  Selecione a data e clique em 'Buscar'
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </div>
    </Geral>
  );
};

export default Grid;

